import type { KeyboardEvent, MouseEvent } from "react";
import { useCallback, useRef, useState } from "react";
import { ArrowRightLeft, ChevronDown } from "lucide-react";
import clsx from "clsx";
import { AnchoredPopover } from "components/common/AnchoredPopover";
import { PanelSelectList } from "./PanelSelectList";
import { PanelSelectDot } from "./PanelSelectDot";
import type { PanelSelectProps } from "./types";

const LIST_HEIGHT = 340;
// An icon trigger is 36px wide, so the list cannot take its width from it.
const ICON_LIST_WIDTH = 260;

export const PanelSelect = ({
  value,
  options,
  placeholder,
  ariaLabel,
  trigger = "field",
  isEdited = false,
  onActiveChange,
  onPick,
  onCreate,
  createLabel,
}: PanelSelectProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState(0);

  const open = useCallback(() => {
    setTriggerWidth(triggerRef.current?.offsetWidth ?? 0);
    setIsOpen(true);
  }, []);

  // Focus goes back where he left it, which a native select gave for free and
  // a listbox has to do by hand.
  const close = useCallback(() => {
    setIsOpen(false);
    // The preview belongs to the open list; leaving it lit would mark a run on
    // the plan that nothing on screen still points at.
    onActiveChange?.(null);
    triggerRef.current?.focus();
  }, [onActiveChange]);

  const pick = useCallback(
    (next: string) => {
      close();
      onPick(next);
    },
    [close, onPick],
  );

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // The rows these sit on isolate a run on the plan when clicked; opening a
      // list must not also change what is drawn.
      event.stopPropagation();
      if (isOpen) {
        close();
        return;
      }
      open();
    },
    [close, isOpen, open],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== "ArrowDown" || isOpen) return;
      event.preventDefault();
      open();
    },
    [isOpen, open],
  );

  const selected = options.find((option) => option.value === value);

  const toneClass = isEdited
    ? "border-blue-300 text-blue-700"
    : "border-slate-300 text-slate-800";

  const isIcon = trigger === "icon";

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        // On the field, only once something is chosen: a tooltip repeating the
        // placeholder printed "Move onto" under a button reading "Move onto…".
        title={isIcon ? ariaLabel : selected?.label}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={clsx(
          "flex h-9 items-center rounded-md border focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500",
          isIcon
            ? "w-9 flex-none justify-center"
            : "w-full min-w-0 gap-2 bg-white pl-2.5 pr-2 text-left text-[14px] font-medium hover:border-slate-400",
          !isIcon && (selected ? toneClass : "border-slate-300 text-slate-500"),
          isIcon &&
            (isOpen
              ? "border-blue-400 bg-blue-50 text-blue-700"
              : "border-transparent text-slate-500 hover:border-slate-300 hover:bg-white hover:text-blue-600"),
        )}
      >
        {isIcon ? (
          <ArrowRightLeft size={17} aria-hidden />
        ) : (
          <>
            {/* Matches the list's own rule, so the closed control and the open
                one do not disagree about whether this kind has a colour. */}
            {selected && options.some((option) => option.color) && (
              <PanelSelectDot color={selected.color} />
            )}
            <span className="min-w-0 flex-1 truncate">
              {selected?.label ?? placeholder}
            </span>
            <ChevronDown
              size={16}
              aria-hidden
              className="flex-none text-slate-500"
            />
          </>
        )}
      </button>

      <AnchoredPopover
        isOpen={isOpen}
        anchorRef={triggerRef}
        onClose={close}
        estimatedHeight={LIST_HEIGHT}
      >
        <PanelSelectList
          options={options}
          value={value}
          ariaLabel={ariaLabel}
          heading={placeholder}
          width={isIcon ? ICON_LIST_WIDTH : triggerWidth}
          onActiveChange={onActiveChange}
          onPick={pick}
          {...(onCreate
            ? {
                onCreate: (text: string) => {
                  close();
                  onCreate(text);
                },
                createLabel,
              }
            : {})}
        />
      </AnchoredPopover>
    </>
  );
};
